if (!customElements.get('ls-quote-form')) {
  customElements.define(
    'ls-quote-form',
    class LsQuoteForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-quote-form]');
        this.zipSelect = this.querySelector('[data-zip-select]');
        this.phoneInput = this.querySelector('[data-phone-input]');
        this.addressInput = this.querySelector('[data-address-input]');
        this.productInputs = Array.from(this.querySelectorAll('.lsqf__chip-input[name="products"]'));
        this.submitButton = this.querySelector('button[type="submit"]');
        this.status = this.querySelector('[data-form-status]');
        this.success = this.querySelector('[data-form-success]');
        this.photosInput = this.querySelector('[data-photos-input]');
        this.dropzoneText = this.querySelector('[data-dropzone-text]');
        this.photosError = this.querySelector('[data-photos-error]');
        this.photosList = this.querySelector('[data-photos-list]');
        this.photosNotice = this.querySelector('[data-photos-notice]');
        this.productCard = this.querySelector('[data-product-card]');
        this.warning = this.querySelector('[data-area-warning]');
        this.warnedFor = '';
        this.fenceNote = this.querySelector('[data-fence-note]');
        this.nextVideo = this.querySelector('.lsqf__next-video');
        this.photosFailed = false;
        this.selectedPhotos = [];
        this.thumbUrls = [];
        this.photoKeys = new Set();
        this.pendingKeys = new Set();
        this.panels = {};
        this.querySelectorAll('.lsqf__panel').forEach((panel) => {
          this.panels[panel.dataset.panel] = panel;
        });

        this.onSubmit = this.onSubmit.bind(this);
        this.onZipChange = this.onZipChange.bind(this);
        this.onDoneClick = this.onDoneClick.bind(this);
        this.onPhotosChange = this.onPhotosChange.bind(this);
        this.onPhotosListClick = this.onPhotosListClick.bind(this);
        this.onPhoneInput = this.onPhoneInput.bind(this);
        this.onProductChange = this.onProductChange.bind(this);
        this.onWarningClick = this.onWarningClick.bind(this);
        this.onWarningKeydown = this.onWarningKeydown.bind(this);

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit);
          this.form.addEventListener('click', this.onDoneClick);
        }
        if (this.photosInput) this.photosInput.addEventListener('change', this.onPhotosChange);
        if (this.photosList) this.photosList.addEventListener('click', this.onPhotosListClick);
        if (this.phoneInput) this.phoneInput.addEventListener('input', this.onPhoneInput);
        if (this.zipSelect) this.zipSelect.addEventListener('change', this.onZipChange);
        this.productInputs.forEach((input) => input.addEventListener('change', this.onProductChange));
        if (this.warning) this.warning.addEventListener('click', this.onWarningClick);
        this.onSubmitGuard = this.onSubmitGuard.bind(this);
        window.addEventListener('click', this.onSubmitGuard, true);
        window.addEventListener('touchstart', this.onSubmitGuard, { capture: true, passive: true });
        this.buildZipCombobox();
        this.buildAddressAutocomplete();
        this.renderLinkedProduct();
        this.renderCallStatus();
        this.loadTurnstile();
      }

      /* ── Call box ─────────────────────────────────────────── */

      /*
        "Open now" or "closed" next to the phone number, from the office hours in
        Chicago time, so a visitor in another time zone still gets the right answer.
        Holidays aren't known here; the hours line under it covers that.
      */
      renderCallStatus() {
        const status = this.querySelector('[data-call-status]');
        if (!status) return;
        const open = LsQuoteForm.officeOpen(
          this.dataset.callOpenDays,
          Number(this.dataset.callOpenHour),
          Number(this.dataset.callCloseHour)
        );
        if (open === null) return;
        status.textContent = open ? status.dataset.openLabel || '' : status.dataset.closedLabel || '';
        status.classList.toggle('is-open', open);
        status.hidden = status.textContent === '';
      }

      static officeOpen(daysText, openHour, closeHour) {
        try {
          const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Chicago',
            weekday: 'short',
            hour: 'numeric',
            hourCycle: 'h23'
          }).formatToParts(new Date());
          const weekday = parts.find((part) => part.type === 'weekday').value;
          const hour = Number(parts.find((part) => part.type === 'hour').value);
          const day = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(weekday) + 1;
          const days = String(daysText || '')
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => value >= 1 && value <= 7);
          return days.indexOf(day) !== -1 && hour >= openHour && hour < closeHour;
        } catch (error) {
          return null;
        }
      }

      loadTurnstile() {
        if (!this.dataset.turnstileSiteKey) return;
        if (document.querySelector('script[data-ls-turnstile]')) return;
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.dataset.lsTurnstile = 'true';
        document.head.appendChild(script);
      }

      async nextToken() {
        const field = this.querySelector('[name="cf-turnstile-response"]');
        if (!field) return '';
        if (!this.tokenUsed) {
          this.tokenUsed = true;
          return field.value;
        }
        if (!window.turnstile) return '';
        field.value = '';
        try {
          window.turnstile.reset();
        } catch (error) {
          return '';
        }
        for (let attempt = 0; attempt < 80; attempt += 1) {
          if (field.value) return field.value;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return '';
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.onSubmit);
          this.form.removeEventListener('click', this.onDoneClick);
        }
        if (this.photosInput) this.photosInput.removeEventListener('change', this.onPhotosChange);
        if (this.photosList) this.photosList.removeEventListener('click', this.onPhotosListClick);
        this.releaseThumbs();
        if (this.phoneInput) this.phoneInput.removeEventListener('input', this.onPhoneInput);
        if (this.zipSelect) this.zipSelect.removeEventListener('change', this.onZipChange);
        this.productInputs.forEach((input) => input.removeEventListener('change', this.onProductChange));
        if (this.warning) this.warning.removeEventListener('click', this.onWarningClick);
        window.removeEventListener('click', this.onSubmitGuard, true);
        window.removeEventListener('touchstart', this.onSubmitGuard, true);
        document.removeEventListener('keydown', this.onWarningKeydown);
        document.body.classList.remove('lsqf-warning-open');
        this.teardownZipCombobox();
        this.teardownAddressAutocomplete();
      }

      /* ── Phone formatting ─────────────────────────────────── */

      /*
        "Apply number formatting" is on in HubSpot, so it validates each number and
        renders the valid ones as clickable, consistently formatted links. Validation
        needs the country, which only a country code states outright — so E.164 is what
        gets submitted. The visible field keeps its (847) 123-4567 mask.
      */
      static toE164(value) {
        const digits = LsQuoteForm.toDigits(value);
        return digits.length === 10 ? '+1' + digits : '';
      }

      static toDigits(value) {
        let digits = String(value || '').replace(/\D/g, '');
        if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
        return digits.slice(0, 10);
      }

      /*
        A number the mask cannot honestly represent. Masking one anyway is what turned
        +92 313 00000 into (923) 130-0000 and shipped it as a US number: ten digits once
        the +92 is stripped, so every length check passed. Left as typed instead, so the
        customer sees what they entered and validation can reject it.
      */
      static isUnmaskable(value) {
        const raw = String(value || '').trim();
        const digits = raw.replace(/\D/g, '');
        if (raw.charAt(0) === '+' && digits.charAt(0) !== '1') return true;
        return digits.length > 11;
      }

      /*
        NANP structure: a US area code and exchange both start 2-9, which is what
        catches a foreign number that happens to land on ten digits. An explicit
        non-+1 country code is refused outright, since digits alone cannot carry it.
      */
      static isValidUsPhone(value) {
        const raw = String(value || '').trim();
        const digits = raw.replace(/\D/g, '');
        if (raw.charAt(0) === '+' && digits.charAt(0) !== '1') return false;
        if (digits.length !== 10 && !(digits.length === 11 && digits.charAt(0) === '1')) return false;
        return /^[2-9]\d{2}[2-9]\d{6}$/.test(LsQuoteForm.toDigits(raw));
      }

      static toDisplayFormat(value) {
        const digits = LsQuoteForm.toDigits(value);
        if (digits.length > 6) {
          return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
        }
        if (digits.length > 3) return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
        if (digits.length > 0) return '(' + digits;
        return '';
      }

      onPhoneInput(event) {
        if (LsQuoteForm.isUnmaskable(event.target.value)) return;
        event.target.value = LsQuoteForm.toDisplayFormat(event.target.value);
      }

      /* ── Panels ───────────────────────────────────────────── */

      showPanel(name) {
        Object.keys(this.panels).forEach((key) => {
          this.panels[key].hidden = key !== name;
        });
        this.hideStatuses();
        const focusTarget = this.panels[name].querySelector('.lsqf__submitted-title');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      onDoneClick(event) {
        const done = event.target.closest('[data-wizard-done]');
        if (done) this.finishDone(done);
      }

      static get MAX_PHOTOS() {
        return 10;
      }

      static get MAX_PHOTO_BYTES() {
        return 10 * 1024 * 1024;
      }

      static get MAX_VIDEO_BYTES() {
        return 25 * 1024 * 1024;
      }

      static get MAX_TOTAL_BYTES() {
        return 60 * 1024 * 1024;
      }

      static get ALLOWED_PHOTO_TYPES() {
        return ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      }

      static get ALLOWED_VIDEO_TYPES() {
        return ['video/mp4', 'video/quicktime', 'video/x-m4v'];
      }

      static get PHOTO_EXTENSIONS() {
        return ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
      }

      static get VIDEO_EXTENSIONS() {
        return ['mp4', 'mov', 'm4v'];
      }

      static get THUMB_TIMEOUT() {
        return 4000;
      }

      photoKey(file) {
        return [file.name, file.size, file.lastModified].join(':');
      }

      fileExtension(file) {
        const name = String(file.name || '');
        const dot = name.lastIndexOf('.');
        return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
      }

      fileCategory(file) {
        const type = String(file.type || '').toLowerCase();
        if (LsQuoteForm.ALLOWED_PHOTO_TYPES.includes(type)) return 'image';
        if (LsQuoteForm.ALLOWED_VIDEO_TYPES.includes(type)) return 'video';
        if (type) return '';
        const extension = this.fileExtension(file);
        if (LsQuoteForm.PHOTO_EXTENSIONS.includes(extension)) return 'image';
        if (LsQuoteForm.VIDEO_EXTENSIONS.includes(extension)) return 'video';
        return '';
      }

      async onPhotosChange() {
        if (!this.photosInput) return;
        const rejected = [];
        let totalBytes = this.selectedBytes();
        const candidates = Array.from(this.photosInput.files);
        const checking = [];

        let hitMax = false;

        for (const file of candidates) {
          const key = this.photoKey(file);
          if (this.photoKeys.has(key)) {
            rejected.push(this.photoMessage('photosDuplicateError', file));
            continue;
          }
          if (this.selectedPhotos.length >= LsQuoteForm.MAX_PHOTOS) {
            hitMax = true;
            continue;
          }
          const problem = this.photoProblem(file);
          if (problem) {
            rejected.push(problem);
            continue;
          }
          if (totalBytes + file.size > LsQuoteForm.MAX_TOTAL_BYTES) {
            rejected.push(this.photoMessage('photosTotalError', file));
            continue;
          }
          totalBytes += file.size;
          this.photoKeys.add(key);
          this.pendingKeys.add(key);
          this.selectedPhotos.push(file);
          checking.push(file);
        }

        if (hitMax) {
          const maxTemplate = this.dataset.photosMaxError || '';
          if (maxTemplate) rejected.push(maxTemplate.replace('[count]', String(LsQuoteForm.MAX_PHOTOS)));
        }

        this.syncPhotosInput();
        this.renderPhotosCount();
        this.renderPhotosList();
        this.showPhotosError(rejected.filter(Boolean));

        const failures = [];
        await Promise.all(
          checking.map(async (file) => {
            const key = this.photoKey(file);
            const readable = await this.isReadable(file);
            this.pendingKeys.delete(key);
            if (!readable) {
              this.photoKeys.delete(key);
              this.selectedPhotos = this.selectedPhotos.filter((entry) => this.photoKey(entry) !== key);
              failures.push(this.photoMessage('photosUnreadableError', file));
            }
            this.syncPhotosInput();
            this.renderPhotosCount();
            this.renderPhotosList();
          })
        );

        if (failures.length) this.showPhotosError(rejected.concat(failures).filter(Boolean));
      }

      verifiedPhotos() {
        return this.selectedPhotos.filter((file) => !this.pendingKeys.has(this.photoKey(file)));
      }

      async isReadable(file) {
        if (!file.size) return false;
        let slice;
        try {
          slice = file.slice(0, 1);
        } catch (error) {
          return true;
        }
        if (!slice || typeof slice.arrayBuffer !== 'function') return true;
        try {
          await slice.arrayBuffer();
          return true;
        } catch (error) {
          console.warn('[ls-quote-form] File could not be read:', file.name, error);
          return false;
        }
      }

      onPhotosListClick(event) {
        const button = event.target.closest('[data-photos-remove]');
        if (!button) return;
        event.preventDefault();
        this.removePhoto(button.dataset.photosRemove);
      }

      removePhoto(key) {
        if (this.pendingKeys.has(key)) return;
        const index = this.selectedPhotos.findIndex((file) => this.photoKey(file) === key);
        if (index === -1) return;
        this.selectedPhotos.splice(index, 1);
        this.photoKeys.delete(key);
        this.syncPhotosInput();
        this.renderPhotosCount();
        this.renderPhotosList();
        this.showPhotosError([]);
      }

      renderPhotosList() {
        if (!this.photosList) return;
        this.photosList.textContent = '';
        this.releaseThumbs();
        this.photosList.hidden = this.selectedPhotos.length === 0;
        this.selectedPhotos.forEach((file) => {
          this.photosList.appendChild(this.buildPhotoItem(file, this.pendingKeys.has(this.photoKey(file))));
        });
      }

      releaseThumbs() {
        this.thumbUrls.forEach((url) => URL.revokeObjectURL(url));
        this.thumbUrls = [];
      }

      typeIcon(category) {
        return category === 'video'
          ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
              '<rect x="3" y="6" width="13" height="12" rx="2" stroke="currentColor" stroke-width="2"/>' +
              '<path d="M16 10l5-3v10l-5-3" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>'
          : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
              '<rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="2"/>' +
              '<circle cx="8.5" cy="9.5" r="1.5" fill="currentColor"/>' +
              '<path d="M4 17l5-5 4 4 3-2 4 4" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>';
      }

      buildThumb(file, pending, onSettle) {
        const category = this.fileCategory(file);
        const thumb = document.createElement('span');
        thumb.className = 'lsqf__file-thumb';
        thumb.innerHTML = '<span class="lsqf__spinner" aria-hidden="true"></span>';

        const fallBackToIcon = () => {
          thumb.innerHTML = this.typeIcon(category);
          onSettle();
        };

        if (pending) return thumb;
        if (typeof URL === 'undefined' || !URL.createObjectURL) {
          fallBackToIcon();
          return thumb;
        }

        const url = URL.createObjectURL(file);
        this.thumbUrls.push(url);

        const preview = document.createElement(category === 'video' ? 'video' : 'img');
        preview.className = 'lsqf__file-thumb-media';
        if (category === 'video') {
          preview.muted = true;
          preview.playsInline = true;
          preview.preload = 'metadata';
        } else {
          preview.alt = '';
        }

        const giveUp = setTimeout(fallBackToIcon, LsQuoteForm.THUMB_TIMEOUT);
        preview.addEventListener(
          category === 'video' ? 'loadeddata' : 'load',
          () => {
            clearTimeout(giveUp);
            thumb.textContent = '';
            thumb.appendChild(preview);
            onSettle();
          },
          { once: true }
        );
        preview.addEventListener(
          'error',
          () => {
            clearTimeout(giveUp);
            fallBackToIcon();
          },
          { once: true }
        );
        preview.src = category === 'video' ? url + '#t=0.1' : url;

        return thumb;
      }

      buildPhotoItem(file, pending) {
        const item = document.createElement('li');
        item.className = 'lsqf__file lsqf__file--loading';

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'lsqf__file-remove';
        remove.dataset.photosRemove = this.photoKey(file);
        remove.disabled = Boolean(pending);
        const removeLabel = this.dataset.photosRemoveLabel || '';
        if (removeLabel) remove.setAttribute('aria-label', removeLabel.replace('[name]', file.name));
        remove.innerHTML =
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
          '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

        const status = document.createElement('span');
        status.className = 'lsqf__file-status';
        status.textContent = this.dataset.photosLoadingLabel || '';

        const settle = () => {
          item.classList.remove('lsqf__file--loading');
          status.remove();
        };

        item.appendChild(this.buildThumb(file, pending, settle));

        const name = document.createElement('span');
        name.className = 'lsqf__file-name';
        name.textContent = file.name;
        item.appendChild(name);

        if (status.textContent) item.appendChild(status);

        const sizeText = this.formatSize(file.size);
        if (sizeText) {
          const size = document.createElement('span');
          size.className = 'lsqf__file-size';
          size.textContent = sizeText;
          item.appendChild(size);
        }

        item.appendChild(remove);
        return item;
      }

      formatSize(bytes) {
        if (bytes >= 1024 * 1024) {
          const format = this.dataset.photosSizeFormatMb || '';
          return format ? format.replace('[size]', (bytes / 1024 / 1024).toFixed(1)) : '';
        }
        const format = this.dataset.photosSizeFormatKb || '';
        return format ? format.replace('[size]', (bytes / 1024).toFixed(1)) : '';
      }

      selectedBytes() {
        return this.selectedPhotos.reduce((total, file) => total + file.size, 0);
      }

      maxBytesFor(file) {
        return this.fileCategory(file) === 'video'
          ? LsQuoteForm.MAX_VIDEO_BYTES
          : LsQuoteForm.MAX_PHOTO_BYTES;
      }

      photoProblem(file) {
        if (!this.fileCategory(file)) {
          return this.photoMessage('photosTypeError', file);
        }
        if (file.size > this.maxBytesFor(file)) {
          return this.photoMessage('photosSizeError', file);
        }
        return '';
      }

      photoMessage(key, file) {
        const template = this.dataset[key] || '';
        if (!template) return '';
        const limit = key === 'photosTotalError' ? LsQuoteForm.MAX_TOTAL_BYTES : this.maxBytesFor(file);
        return template.replace('[name]', file.name).replace('[size]', String(limit / 1024 / 1024));
      }

      showPhotosError(messages) {
        if (!this.photosError) return;
        if (!messages.length) {
          this.photosError.hidden = true;
          this.photosError.textContent = '';
          return;
        }
        this.photosError.textContent = messages.join(' ');
        this.photosError.hidden = false;
      }

      showPhotosNotice() {
        if (!this.photosNotice) return;
        const message = this.dataset.photosUploadError || '';
        if (!this.photosFailed || !message) {
          this.photosNotice.hidden = true;
          return;
        }
        this.photosNotice.textContent = message;
        this.photosNotice.hidden = false;
      }

      syncPhotosInput() {
        if (!this.photosInput || typeof DataTransfer === 'undefined') return;
        const transfer = new DataTransfer();
        this.verifiedPhotos().forEach((file) => transfer.items.add(file));
        this.photosInput.files = transfer.files;
      }

      renderPhotosCount() {
        if (!this.dropzoneText) return;
        if (!this.dropzoneText.dataset.placeholder) {
          this.dropzoneText.dataset.placeholder = this.dropzoneText.textContent;
        }
        this.dropzoneText.textContent =
          this.selectedPhotos.length === 0
            ? this.dropzoneText.dataset.placeholder
            : this.photosCountText(this.selectedPhotos.length);
      }

      photosCountText(count) {
        const many = this.dataset.photosCountFormat || '[count] files attached';
        const one = this.dataset.photosCountFormatOne || many;
        return (count === 1 ? one : many).replace('[count]', String(count));
      }

      /* ── Address autocomplete (Google Places) ─────────────── */

      /*
        The Maps JS API can only be loaded once per page, so the promise lives on the
        class rather than the instance — several quote forms can share a single load.
        Loading is deferred until the customer focuses the address field: Places is
        billed per request, and most visitors never touch this optional field.
      */
      static loadPlacesLibrary(apiKey) {
        if (LsQuoteForm.placesPromise) return LsQuoteForm.placesPromise;
        LsQuoteForm.placesPromise = new Promise((resolve, reject) => {
          if (window.google && window.google.maps && window.google.maps.importLibrary) {
            resolve();
            return;
          }
          const callbackName = 'lsQuoteFormMapsReady';
          window[callbackName] = () => {
            delete window[callbackName];
            resolve();
          };
          const script = document.createElement('script');
          script.async = true;
          script.src =
            'https://maps.googleapis.com/maps/api/js?loading=async&libraries=places&callback=' +
            callbackName +
            '&key=' +
            encodeURIComponent(apiKey);
          script.onerror = () => {
            delete window[callbackName];
            reject(new Error('Google Maps failed to load'));
          };
          document.head.appendChild(script);
        }).then(() => window.google.maps.importLibrary('places'));
        return LsQuoteForm.placesPromise;
      }

      /*
        Wraps the plain address input in the same combobox markup the town picker uses,
        so suggestions inherit the theme styling instead of Google's own widget.
        Without an API key the input is left exactly as rendered.
      */
      buildAddressAutocomplete() {
        if (!this.addressInput || !this.dataset.mapsApiKey) return;

        const listboxId = this.addressInput.id + '-listbox';
        const combobox = document.createElement('div');
        combobox.className = 'lsqf__combobox lsqf__combobox--address';

        const listbox = document.createElement('ul');
        listbox.className = 'lsqf__combobox-listbox';
        listbox.id = listboxId;
        listbox.setAttribute('role', 'listbox');
        listbox.hidden = true;

        this.addressInput.insertAdjacentElement('afterend', combobox);
        combobox.appendChild(this.addressInput);
        combobox.appendChild(listbox);

        this.addressInput.setAttribute('role', 'combobox');
        this.addressInput.setAttribute('aria-expanded', 'false');
        this.addressInput.setAttribute('aria-controls', listboxId);
        this.addressInput.setAttribute('aria-autocomplete', 'list');
        this.addressInput.setAttribute('autocomplete', 'off');

        this.addressListbox = listbox;
        this.addressListboxId = listboxId;
        this.addressActiveIndex = -1;
        this.addressDebounce = 0;
        this.addressRequestId = 0;

        this.onAddressInput = this.onAddressInput.bind(this);
        this.onAddressFocus = this.onAddressFocus.bind(this);
        this.onAddressKeydown = this.onAddressKeydown.bind(this);
        this.onAddressBlur = this.onAddressBlur.bind(this);
        this.onAddressListClick = this.onAddressListClick.bind(this);

        this.addressInput.addEventListener('input', this.onAddressInput);
        this.addressInput.addEventListener('focus', this.onAddressFocus);
        this.addressInput.addEventListener('keydown', this.onAddressKeydown);
        this.addressInput.addEventListener('blur', this.onAddressBlur);
        listbox.addEventListener('mousedown', this.onAddressListClick);
      }

      teardownAddressAutocomplete() {
        if (!this.addressListbox) return;
        window.clearTimeout(this.addressDebounce);
        this.addressInput.removeEventListener('input', this.onAddressInput);
        this.addressInput.removeEventListener('focus', this.onAddressFocus);
        this.addressInput.removeEventListener('keydown', this.onAddressKeydown);
        this.addressInput.removeEventListener('blur', this.onAddressBlur);
        this.addressListbox.removeEventListener('mousedown', this.onAddressListClick);
      }

      /*
        Chicagoland, northern Illinois and southern Wisconsin — the service area.
        This biases ranking, it does not restrict: an address outside the box still
        appears, just lower. Without it Google ranks purely on text, so "742 Hartmann"
        returns Texas and Tennessee ahead of Schaumburg.
      */
      static get SERVICE_AREA_BIAS() {
        return {
          north: 43.2,
          south: 41.4,
          east: -87.5,
          west: -88.5
        };
      }

      async fetchAddressSuggestions(query) {
        const places = await LsQuoteForm.loadPlacesLibrary(this.dataset.mapsApiKey);
        if (!this.addressSessionToken) {
          this.addressSessionToken = new places.AutocompleteSessionToken();
        }
        const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken: this.addressSessionToken,
          includedRegionCodes: ['us'],
          /* Street addresses only — the required ZIP field already pins down the area. */
          includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
          locationBias: LsQuoteForm.SERVICE_AREA_BIAS
        });
        return (result.suggestions || [])
          .map((suggestion) => suggestion.placePrediction)
          .filter(Boolean)
          .map((prediction) => prediction.text.toString());
      }

      renderAddressSuggestions(items) {
        this.addressListbox.innerHTML = '';
        items.forEach((text, index) => {
          const option = document.createElement('li');
          option.className = 'lsqf__combobox-option';
          option.id = this.addressListboxId + '-option-' + index;
          option.setAttribute('role', 'option');
          option.textContent = text;
          this.addressListbox.appendChild(option);
        });
        this.setAddressActiveIndex(-1);
        const open = items.length > 0;
        this.addressListbox.hidden = !open;
        this.addressInput.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      addressOptionEls() {
        return this.addressListbox ? Array.from(this.addressListbox.children) : [];
      }

      setAddressActiveIndex(index) {
        const options = this.addressOptionEls();
        options.forEach((option) => option.classList.remove('is-active'));
        this.addressActiveIndex = index;
        const active = options[index];
        if (active) {
          active.classList.add('is-active');
          active.scrollIntoView({ block: 'nearest' });
          this.addressInput.setAttribute('aria-activedescendant', active.id);
        } else {
          this.addressInput.removeAttribute('aria-activedescendant');
        }
      }

      closeAddressListbox() {
        if (!this.addressListbox) return;
        this.addressListbox.hidden = true;
        this.addressInput.setAttribute('aria-expanded', 'false');
        this.setAddressActiveIndex(-1);
      }

      selectAddressOption(option) {
        if (!option) return;
        this.addressInput.value = option.textContent;
        /* A session ends with a selection — the next search starts a fresh one. */
        this.addressSessionToken = null;
        this.closeAddressListbox();
      }

      onAddressFocus() {
        /* Warm the library up on focus so the first keystroke already has it ready. */
        LsQuoteForm.loadPlacesLibrary(this.dataset.mapsApiKey).catch(() => {});
      }

      onAddressInput() {
        window.clearTimeout(this.addressDebounce);
        const query = this.addressInput.value.trim();
        if (query.length < 3) {
          this.closeAddressListbox();
          return;
        }
        this.addressRequestId += 1;
        const requestId = this.addressRequestId;
        this.addressDebounce = window.setTimeout(async () => {
          try {
            const items = await this.fetchAddressSuggestions(query);
            /* A slower earlier request must not overwrite a newer one. */
            if (requestId !== this.addressRequestId) return;
            this.renderAddressSuggestions(items);
          } catch (error) {
            /* Autocomplete is a convenience — the customer can always type it out. */
            this.closeAddressListbox();
          }
        }, 250);
      }

      onAddressBlur() {
        window.setTimeout(() => this.closeAddressListbox(), 120);
      }

      onAddressListClick(event) {
        const option = event.target.closest('.lsqf__combobox-option');
        if (!option) return;
        event.preventDefault();
        this.selectAddressOption(option);
      }

      onAddressKeydown(event) {
        const options = this.addressOptionEls();
        if (this.addressListbox.hidden || options.length === 0) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.setAddressActiveIndex(Math.min(this.addressActiveIndex + 1, options.length - 1));
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.setAddressActiveIndex(Math.max(this.addressActiveIndex - 1, 0));
        } else if (event.key === 'Enter') {
          const active = options[this.addressActiveIndex];
          if (active) {
            /* Stop the form submitting while the customer is still picking. */
            event.preventDefault();
            this.selectAddressOption(active);
          }
        } else if (event.key === 'Escape') {
          this.closeAddressListbox();
        }
      }

      /* ── Product to quote (from the ?product= link) ────────── */

      renderLinkedProduct() {
        if (!this.productCard || !this.productCard.hidden) return;
        const handle = new URLSearchParams(window.location.search).get('product');
        if (!handle || handle.length > 255 || handle.indexOf('/') !== -1) return;
        this.fetchLinkedProduct(handle);
      }

      async fetchLinkedProduct(handle) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        let product = null;
        try {
          const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js');
          if (!response.ok) return;
          product = await response.json();
        } catch (error) {
          return;
        }
        if (product && product.title) this.fillLinkedProduct(product);
      }

      fillLinkedProduct(product) {
        const title = this.productCard.querySelector('[data-product-title]');
        if (title) title.textContent = product.title;

        const priceValue = this.productCard.querySelector('[data-product-price-value]');
        const cents = typeof product.price_min === 'number' ? product.price_min : product.price;
        if (priceValue && typeof cents === 'number' && cents > 0) {
          priceValue.textContent = LsQuoteForm.formatMoney(cents, this.dataset.currency);
        }

        const imageWrap = this.productCard.querySelector('[data-product-image]');
        if (imageWrap && product.featured_image) {
          const img = document.createElement('img');
          img.className = 'lsqf__product-img';
          img.loading = 'lazy';
          img.width = 66;
          img.height = 66;
          img.alt = product.title;
          img.src = LsQuoteForm.sizedImageUrl(product.featured_image, 132);
          imageWrap.innerHTML = '';
          imageWrap.appendChild(img);
          imageWrap.hidden = false;
        }

        const productUrl = this.form && this.form.elements.product_to_quote;
        if (productUrl) {
          productUrl.value = window.location.origin + (product.url || '/products/' + product.handle);
        }

        this.productCard.hidden = false;
      }

      static formatMoney(cents, currency) {
        const amount = cents / 100;
        const digits = cents % 100 === 0 ? 0 : 2;
        try {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: digits,
            maximumFractionDigits: digits
          }).format(amount);
        } catch (error) {
          return '$' + amount.toFixed(digits);
        }
      }

      static sizedImageUrl(url, width) {
        const absolute = url.indexOf('//') === 0 ? 'https:' + url : url;
        return absolute + (absolute.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
      }

      /* ── ZIP selection ────────────────────────────────────── */

      /*
        Picking a ZIP does not touch the product chips. Checking them for the
        visitor read as a choice they had already made, so the pick stays
        theirs — coverage is explained by the town disclaimer below the field.
      */
      onZipChange() {
        this.setFieldError(this.zipSelect, false);
        this.checkFenceArea();
      }

      onProductChange() {
        this.checkFenceArea();
        this.syncNoFenceTags();
        if (this.selectedProducts().length > 0) this.setFieldError(this.productInputs[0], false);
      }

      /* Returns whether the popup opened, so submission can stop on it. */
      checkFenceArea() {
        return this.fenceLocked() ? this.enforceFenceLock() : this.maybeWarnOutOfArea();
      }

      fillText(text) {
        const option = this.selectedTownOption();
        return String(text || '')
          .replace(/\[town\]/g, option ? option.dataset.town || '' : '')
          .replace(/\[zip\]/g, this.warningZip || '')
          .replace(/\[phone\]/g, this.dataset.phone || '');
      }

      /* ── Out-of-area fence warning ─────────────────────────── */

      /*
        Every ZIP option carries the coverage of the town it belongs to, so this
        keys off the town rather than the ZIP: one ZIP can span a town we fence
        and a town we don't, and the customer told us which one is theirs.
      */
      selectedTownOption() {
        if (!this.zipSelect || !this.zipSelect.value) return null;
        return this.zipSelect.selectedOptions[0] || null;
      }

      /* No town picked yet is not an out-of-area town — say nothing until it is. */
      townServesFences() {
        const option = this.selectedTownOption();
        if (!option) return true;
        return String(option.dataset.products || '').split(',').indexOf('fences') !== -1;
      }

      wantsFences() {
        return this.selectedProducts().indexOf('fences') !== -1;
      }

      outOfAreaKey() {
        const option = this.selectedTownOption();
        return option ? option.value + '|' + (option.dataset.town || '') : '';
      }

      /*
        One warning per town: re-ticking Fences for a town they have already been
        warned about tells them nothing new, but picking a different town does.
        Returns whether it opened, so submission can use it as a backstop.
      */
      maybeWarnOutOfArea() {
        if (!this.warning) return false;
        if (this.townServesFences() || !this.wantsFences()) return false;
        const key = this.outOfAreaKey();
        if (!key || this.warnedFor === key) return false;
        this.warnedFor = key;
        this.openWarning('soft');
        return true;
      }

      /* ── Fence lock (Jake, Oct 20: no fence leads from towns we don't fence) ── */

      fenceLocked() {
        return this.dataset.fenceLock === 'true' && Boolean(this.warning);
      }

      fencesInput() {
        return this.productInputs.find((input) => input.value === 'fences') || null;
      }

      /*
        A town we don't fence greys the Fences chip and says why under the chips.
        Ticking it anyway, or picking that town with Fences already ticked, unticks
        it and opens the popup every time: the lead would only end in a callback
        saying no. The chip stays clickable so the reason is never more than a
        click away.
      */
      enforceFenceLock() {
        const blocked = !this.townServesFences();
        this.setFenceUnavailable(blocked);
        if (!blocked || !this.wantsFences()) return false;
        const input = this.fencesInput();
        if (input) input.checked = false;
        this.syncNoFenceTags();
        this.openWarning('block');
        return true;
      }

      setFenceUnavailable(blocked) {
        const input = this.fencesInput();
        const chip = input ? input.closest('.lsqf__chip') : null;
        if (chip) chip.classList.toggle('lsqf__chip--unavailable', blocked);
        if (!this.fenceNote) return;
        const text = blocked ? this.fillText(this.dataset.fenceLockedNote) : '';
        this.fenceNote.textContent = text;
        this.fenceNote.hidden = text === '';
      }

      fillWarning(mode) {
        const data = this.warning.dataset;
        const texts = {
          soft: [data.title, data.body],
          block: [data.blockTitle, data.blockBody],
          unlisted: [data.unlistedTitle, data.unlistedBody]
        }[mode] || [data.title, data.body];
        const title = this.warning.querySelector('[data-area-warning-title]');
        const body = this.warning.querySelector('[data-area-warning-body]');
        if (title) title.textContent = this.fillText(texts[0]);
        if (body) body.textContent = this.fillText(texts[1]);
      }

      openWarning(mode) {
        this.warning.dataset.mode = mode || 'soft';
        this.fillWarning(this.warning.dataset.mode);
        this.warningReturnFocus = document.activeElement;
        this.warning.hidden = false;
        document.body.classList.add('lsqf-warning-open');
        document.addEventListener('keydown', this.onWarningKeydown);
        const title = this.warning.querySelector('[data-area-warning-title]');
        if (title) title.focus({ preventScroll: true });
      }

      closeWarning() {
        if (!this.warning || this.warning.hidden) return;
        this.warning.hidden = true;
        document.body.classList.remove('lsqf-warning-open');
        document.removeEventListener('keydown', this.onWarningKeydown);
        const target = this.warningReturnFocus;
        this.warningReturnFocus = null;
        /* After "is this ZIP right?", the next thing to do is fix the ZIP. */
        if (this.warning.dataset.mode === 'unlisted' && this.zipComboboxInput) {
          this.zipComboboxInput.focus({ preventScroll: true });
          this.zipComboboxInput.select();
          return;
        }
        if (target && typeof target.focus === 'function' && document.contains(target)) {
          target.focus({ preventScroll: true });
        }
      }

      onWarningClick(event) {
        if (event.target.closest('[data-area-warning-dismiss]')) this.closeWarning();
      }

      onWarningKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeWarning();
          return;
        }
        if (event.key !== 'Tab') return;
        /* aria-modal promises focus stays inside, so Tab wraps at both ends. */
        const items = Array.from(
          this.warning.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')
        );
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !this.warning.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ── ZIP search combobox (progressive enhancement over the select) ── */

      buildZipCombobox() {
        if (!this.zipSelect) return;
        const wrap = this.zipSelect.closest('.lsqf__field--zip');
        if (!wrap) return;

        const listboxId = this.zipSelect.id + '-listbox';
        const combobox = document.createElement('div');
        combobox.className = 'lsqf__combobox';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'lsqf__input lsqf__combobox-input';
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-controls', listboxId);
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('autocomplete', 'off');
        input.placeholder = this.zipSelect.options[0] ? this.zipSelect.options[0].text : '';

        const listbox = document.createElement('ul');
        listbox.className = 'lsqf__combobox-listbox';
        listbox.id = listboxId;
        listbox.setAttribute('role', 'listbox');
        listbox.hidden = true;

        const noFenceTag = this.dataset.zipNoFenceTag || '';
        const options = Array.from(this.zipSelect.options).filter((option) => option.value !== '');
        options.forEach((option, index) => {
          const item = document.createElement('li');
          item.className = 'lsqf__combobox-option';
          item.id = listboxId + '-option-' + index;
          item.setAttribute('role', 'option');
          item.dataset.value = option.value;
          item.dataset.optionIndex = option.index;
          item.dataset.search = option.text.toLowerCase();
          item.dataset.label = option.text;
          item.textContent = option.text;
          /* Shown only while Fences is ticked, so nobody picks a town we don't fence by surprise. */
          const products = String(option.dataset.products || '').split(',');
          if (noFenceTag && products.indexOf('fences') === -1) {
            const tag = document.createElement('span');
            tag.className = 'lsqf__combobox-tag';
            tag.textContent = noFenceTag;
            item.appendChild(tag);
          }
          listbox.appendChild(item);
        });

        /*
          A full ZIP with no match gets one extra row. When unlisted ZIPs are
          accepted, picking it sends the form anyway. When they aren't (the default
          since Oct 20), the row only says so and picking it opens the "is this
          ZIP right?" popup: usually it's a typo.
        */
        this.zipUnlistedLocked = this.dataset.unlistedZip !== 'true';
        const unlisted = document.createElement('li');
        unlisted.className = 'lsqf__combobox-option lsqf__combobox-option--unlisted';
        if (this.zipUnlistedLocked) {
          unlisted.classList.add('lsqf__combobox-option--locked');
          unlisted.setAttribute('aria-disabled', 'true');
        }
        unlisted.id = listboxId + '-option-unlisted';
        unlisted.setAttribute('role', 'option');
        unlisted.hidden = true;
        listbox.prepend(unlisted);
        this.zipUnlistedItem = unlisted;

        combobox.appendChild(input);
        combobox.appendChild(listbox);
        const selectWrap = this.zipSelect.closest('.lsqf__select-wrap') || this.zipSelect;
        selectWrap.insertAdjacentElement('afterend', combobox);
        wrap.classList.add('lsqf__field--zip-enhanced');

        this.zipCombobox = combobox;
        this.zipComboboxInput = input;
        this.zipComboboxListbox = listbox;
        this.zipComboboxActiveIndex = -1;
        this.syncNoFenceTags();

        this.onComboboxInput = this.onComboboxInput.bind(this);
        this.onComboboxKeydown = this.onComboboxKeydown.bind(this);
        this.onComboboxFocus = this.onComboboxFocus.bind(this);
        this.onComboboxBlur = this.onComboboxBlur.bind(this);
        this.onComboboxListClick = this.onComboboxListClick.bind(this);

        input.addEventListener('input', this.onComboboxInput);
        input.addEventListener('keydown', this.onComboboxKeydown);
        input.addEventListener('focus', this.onComboboxFocus);
        input.addEventListener('blur', this.onComboboxBlur);
        listbox.addEventListener('mousedown', this.onComboboxListClick);
      }

      teardownZipCombobox() {
        if (!this.zipComboboxInput) return;
        this.zipComboboxInput.removeEventListener('input', this.onComboboxInput);
        this.zipComboboxInput.removeEventListener('keydown', this.onComboboxKeydown);
        this.zipComboboxInput.removeEventListener('focus', this.onComboboxFocus);
        this.zipComboboxInput.removeEventListener('blur', this.onComboboxBlur);
        if (this.zipComboboxListbox) {
          this.zipComboboxListbox.removeEventListener('mousedown', this.onComboboxListClick);
        }
      }

      comboboxOptionEls() {
        return this.zipComboboxListbox ? Array.from(this.zipComboboxListbox.children) : [];
      }

      /* The text that goes in the field, without the "No fences" tag. */
      itemLabel(item) {
        return item.dataset.label || item.textContent;
      }

      syncNoFenceTags() {
        if (!this.zipComboboxListbox) return;
        this.zipComboboxListbox.classList.toggle('lsqf__combobox-listbox--fences', this.wantsFences());
      }

      openCombobox() {
        this.zipComboboxListbox.hidden = false;
        this.zipComboboxInput.setAttribute('aria-expanded', 'true');
      }

      closeCombobox() {
        this.zipComboboxListbox.hidden = true;
        this.zipComboboxInput.setAttribute('aria-expanded', 'false');
        this.zipComboboxInput.removeAttribute('aria-activedescendant');
        this.setComboboxActiveIndex(-1);
      }

      filterCombobox(query) {
        const term = query.trim().toLowerCase();
        this.comboboxOptionEls().forEach((item) => {
          if (item === this.zipUnlistedItem) return;
          item.hidden = term !== '' && !item.dataset.search.includes(term);
        });
        if (this.zipUnlistedItem) {
          const show = LsQuoteForm.isZip(term) && this.listedZipItems(term).length === 0;
          this.zipUnlistedItem.hidden = !show;
          if (show) {
            const label = this.zipUnlistedLocked
              ? this.dataset.unlistedZipLockedLabel || '[zip]'
              : this.dataset.unlistedZipLabel || '[zip]';
            this.zipUnlistedItem.dataset.value = term;
            this.zipUnlistedItem.textContent = label
              .replace(/\[zip\]/g, term)
              .replace(/\[phone\]/g, this.dataset.phone || '');
          }
        }
        this.setComboboxActiveIndex(-1);
      }

      static isZip(text) {
        return /^\d{5}$/.test(text);
      }

      listedZipItems(zip) {
        return this.comboboxOptionEls().filter(
          (item) => item !== this.zipUnlistedItem && item.dataset.value === zip
        );
      }

      /*
        The select only holds listed ZIPs, so an unlisted one gets a single extra
        option, reused if the customer changes their mind. It counts as covering
        everything: we don't know the area, so the office checks rather than the
        form warning them off.
      */
      useUnlistedZip(zip) {
        let option = this.zipSelect.querySelector('option[data-unlisted]');
        if (!option) {
          option = document.createElement('option');
          option.dataset.unlisted = '';
          this.zipSelect.appendChild(option);
        }
        option.value = zip;
        option.textContent = zip;
        option.dataset.town = this.dataset.unlistedZipTown || '';
        option.dataset.products = 'fences,playsets,hoops,trampolines';
        this.zipSelect.selectedIndex = option.index;
      }

      /*
        People type their ZIP and move on without picking from the list. If what
        they typed is a full ZIP with one answer, take it, so the form doesn't
        reject a ZIP that is plainly there. Several towns sharing it still needs a pick.
      */
      commitTypedZip(insist) {
        if (!this.zipComboboxInput || this.zipSelect.value) return false;
        const typed = this.zipComboboxInput.value.trim();
        if (!LsQuoteForm.isZip(typed)) return false;
        const listed = this.listedZipItems(typed);
        if (listed.length === 1) {
          this.selectComboboxOption(listed[0]);
          return true;
        }
        if (listed.length === 0 && this.zipUnlistedItem) {
          if (this.zipUnlistedLocked) {
            this.rejectUnlistedZip(typed, insist);
            return true;
          }
          this.filterCombobox(typed);
          this.selectComboboxOption(this.zipUnlistedItem);
          return true;
        }
        return false;
      }

      /* The typed text, when it is a full ZIP we don't serve and can't take. */
      rejectedTypedZip() {
        if (!this.zipUnlistedLocked || !this.zipComboboxInput || this.zipSelect.value) return '';
        const typed = this.zipComboboxInput.value.trim();
        return LsQuoteForm.isZip(typed) && this.listedZipItems(typed).length === 0 ? typed : '';
      }

      /*
        The message under the field stays until they change the ZIP. The popup opens
        once per ZIP when they just move on (blur), and every time they try to send
        it or pick the row (insist), since that's when it has to be explained.
      */
      rejectUnlistedZip(zip, insist) {
        this.warningZip = zip;
        this.setFieldError(this.zipSelect, true, this.fillText(this.dataset.unlistedZipError));
        if (!this.warning) return;
        if (!insist && this.rejectedZip === zip) return;
        this.rejectedZip = zip;
        if (this.zipComboboxListbox) this.closeCombobox();
        this.openWarning('unlisted');
      }

      setComboboxActiveIndex(index) {
        const options = this.comboboxOptionEls();
        options.forEach((item) => item.classList.remove('is-active'));
        this.zipComboboxActiveIndex = index;
        const active = options[index];
        if (active && !active.hidden) {
          active.classList.add('is-active');
          active.scrollIntoView({ block: 'nearest' });
          this.zipComboboxInput.setAttribute('aria-activedescendant', active.id);
        } else {
          this.zipComboboxInput.removeAttribute('aria-activedescendant');
        }
      }

      /*
        Selecting by index, not by value: ZIPs are not unique here. 60047 is
        Buffalo Grove (fences) and also Hawthorn Woods, Kildeer, Lake Zurich and
        Long Grove (no fences), and `select.value = '60047'` lands on whichever
        comes first. That recorded the wrong town in HubSpot and, now that the
        coverage warning reads the picked option, would have skipped the warning
        for the very towns on the boundary.
      */
      selectComboboxOption(item) {
        if (!item) return;
        if (item === this.zipUnlistedItem && this.zipUnlistedLocked) {
          this.rejectUnlistedZip(item.dataset.value, true);
          return;
        }
        this.zipComboboxInput.value = this.itemLabel(item);
        this.zipComboboxInput.dataset.selectedLabel = this.itemLabel(item);
        const index = Number(item.dataset.optionIndex);
        if (item === this.zipUnlistedItem) {
          this.useUnlistedZip(item.dataset.value);
        } else if (Number.isInteger(index) && this.zipSelect.options[index]) {
          this.zipSelect.selectedIndex = index;
        } else {
          this.zipSelect.value = item.dataset.value;
        }
        this.zipSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.closeCombobox();
      }

      onComboboxInput(event) {
        if (this.zipSelect.getAttribute('aria-invalid') === 'true') this.setFieldError(this.zipSelect, false);
        this.filterCombobox(event.target.value);
        if (this.zipSelect.value && event.target.value !== this.zipComboboxInput.dataset.selectedLabel) {
          this.zipSelect.value = '';
          this.zipSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.openCombobox();
      }

      onComboboxFocus() {
        this.filterCombobox(this.zipComboboxInput.value);
        this.openCombobox();
      }

      onComboboxBlur() {
        this.commitTypedZip();
        window.setTimeout(() => this.closeCombobox(), 120);
      }

      onComboboxListClick(event) {
        const item = event.target.closest('.lsqf__combobox-option');
        if (!item) return;
        event.preventDefault();
        this.selectComboboxOption(item);
      }

      onComboboxKeydown(event) {
        const visible = this.comboboxOptionEls().filter((item) => !item.hidden);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (this.zipComboboxListbox.hidden) {
            this.openCombobox();
            this.filterCombobox(this.zipComboboxInput.value);
          }
          const currentPos = visible.indexOf(this.comboboxOptionEls()[this.zipComboboxActiveIndex]);
          const next = visible[Math.min(currentPos + 1, visible.length - 1)] || visible[0];
          this.setComboboxActiveIndex(this.comboboxOptionEls().indexOf(next));
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          const currentPos = visible.indexOf(this.comboboxOptionEls()[this.zipComboboxActiveIndex]);
          const prev = visible[Math.max(currentPos - 1, 0)] || visible[visible.length - 1];
          this.setComboboxActiveIndex(this.comboboxOptionEls().indexOf(prev));
        } else if (event.key === 'Enter') {
          const active = this.comboboxOptionEls()[this.zipComboboxActiveIndex];
          if (active && !active.hidden) {
            event.preventDefault();
            this.selectComboboxOption(active);
          } else if (this.commitTypedZip(true)) {
            event.preventDefault();
          }
        } else if (event.key === 'Escape') {
          this.closeCombobox();
        }
      }

      selectedProducts() {
        return this.productInputs.filter((input) => input.checked).map((input) => input.value);
      }

      selectedProductLabels() {
        return this.productInputs
          .filter((input) => input.checked)
          .map((input) => {
            const label = input.closest('.lsqf__chip').querySelector('.lsqf__chip-label');
            return label ? label.textContent.trim() : input.value;
          });
      }

      /* ── Validation ───────────────────────────────────────── */

      setFieldError(element, hasError, message) {
        const field = element.closest('.lsqf__field');
        element.setAttribute('aria-invalid', hasError ? 'true' : 'false');
        if (!field) return;
        field.classList.toggle('lsqf__field--error', hasError);

        let note = field.querySelector('.lsqf__error-msg');
        if (hasError && message) {
          if (!note) {
            note = document.createElement('p');
            note.className = 'lsqf__error-msg';
            if (element.id) note.id = element.id + '-error';
            field.appendChild(note);
          }
          note.textContent = message;
          if (note.id) element.setAttribute('aria-describedby', note.id);
        } else if (note) {
          element.removeAttribute('aria-describedby');
          note.remove();
        }
      }

      validateMain() {
        const invalid = [];
        const firstName = this.form.elements.first_name;
        const lastName = this.form.elements.last_name;
        const phone = this.form.elements.phone;
        const email = this.form.elements.email;

        [firstName, lastName].forEach((input) => {
          if (!input) return;
          const empty = input.value.trim() === '';
          this.setFieldError(input, empty);
          if (empty) invalid.push(input);
        });

        if (phone) {
          /* Counting ten digits is not enough: a foreign number can reach ten once its
             country code is stripped, and HubSpot then flags the +1 we stamped on it. */
          const bad = !LsQuoteForm.isValidUsPhone(phone.value);
          this.setFieldError(phone, bad, this.dataset.phoneError);
          if (bad) invalid.push(phone);
        }

        if (email) {
          const bad = email.value.trim() === '' || !LsQuoteForm.isEmailAcceptable(email);
          this.setFieldError(email, bad, this.dataset.emailError);
          if (bad) invalid.push(email);
        }

        if (this.productInputs.length > 0) {
          const none = this.selectedProducts().length === 0;
          this.setFieldError(this.productInputs[0], none, this.dataset.productsError);
          if (none) invalid.push(this.productInputs[0]);
        }

        if (this.zipSelect) {
          const empty = this.zipSelect.value === '';
          const rejected = empty ? this.rejectedTypedZip() : '';
          if (rejected) this.warningZip = rejected;
          this.setFieldError(this.zipSelect, empty, rejected ? this.fillText(this.dataset.unlistedZipError) : '');
          if (empty) invalid.push(this.zipSelect);
        }

        /* An open popup already says what's wrong and keeps focus inside it. */
        const popupOpen = this.warning && !this.warning.hidden;
        if (invalid.length > 0 && !popupOpen) invalid[0].focus();
        return invalid.length === 0;
      }

      /* ── Submit guard (WhatConverts) ──────────────────────── */

      /*
        WhatConverts records a lead the moment the submit button is clicked or
        touched: it listens on the button itself, before this form has checked
        anything. So a click that won't send stops here, at the window, before it
        reaches the button. WhatConverts never sees it and the form shows what's
        missing. A click that will send passes untouched, so WhatConverts and
        HubSpot get the same leads. Pressing Enter in a field counts as a click on
        the button, so it goes through here too.
      */
      onSubmitGuard(event) {
        const button = this.submitButton;
        if (!button || !event.target || !button.contains(event.target)) return;
        if (this.readyToSend()) return;
        event.stopImmediatePropagation();
        /* A touch is followed by a click; the click is what explains and blocks. */
        if (event.type !== 'click') return;
        event.preventDefault();
        this.explainNotReady();
      }

      /* The same checks as sending, without touching the page. */
      readyToSend() {
        const fields = this.form.elements;
        const filled = (input) => !input || input.value.trim() !== '';
        if (!filled(fields.first_name) || !filled(fields.last_name)) return false;
        if (fields.phone && !LsQuoteForm.isValidUsPhone(fields.phone.value)) return false;
        if (fields.email && (fields.email.value.trim() === '' || !LsQuoteForm.isEmailAcceptable(fields.email))) return false;
        if (this.productInputs.length > 0 && this.selectedProducts().length === 0) return false;
        if (this.zipSelect && this.zipSelect.value === '') return false;
        if (this.wantsFences() && !this.townServesFences()) {
          if (this.fenceLocked()) return false;
          if (this.warning && this.warnedFor !== this.outOfAreaKey()) return false;
        }
        return true;
      }

      explainNotReady() {
        this.hideStatuses();
        this.commitTypedZip(true);
        if (this.checkFenceArea()) return;
        this.validateMain();
        /* Picking up a typed ZIP can be the only thing that was missing. */
        if (this.readyToSend()) this.submitButton.click();
      }

      /* ── Submission ───────────────────────────────────────── */

      getCookie(name) {
        const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[2]) : '';
      }

      buildContext() {
        const context = {
          pageUri: window.location.href,
          pageName: document.title
        };
        const hutk = this.getCookie('hubspotutk');
        if (hutk) context.hutk = hutk;
        return context;
      }

      buildBasicFields() {
        const fields = [
          { objectTypeId: '0-1', name: 'firstname', value: this.form.elements.first_name.value.trim() },
          { objectTypeId: '0-1', name: 'lastname', value: this.form.elements.last_name.value.trim() },
          { objectTypeId: '0-1', name: 'phone', value: LsQuoteForm.toE164(this.form.elements.phone.value) },
          { objectTypeId: '0-1', name: 'products_of_interest', value: this.selectedProductLabels().join(';') }
        ];
        const email = this.form.elements.email;
        if (email && email.value.trim() !== '') {
          fields.push({ objectTypeId: '0-1', name: 'email', value: email.value.trim() });
        }
        if (this.zipSelect) {
          fields.push({ objectTypeId: '0-1', name: 'zip', value: this.zipSelect.value });
          /* The chosen option names its town outright, so the value is always exact.
             It goes to both `city` and `town`: `city` is HubSpot's own field, `town`
             the custom one, and either being absent from the form costs only itself. */
          const option = this.zipSelect.selectedOptions[0];
          const town = option ? option.dataset.town : '';
          if (town) {
            fields.push({ objectTypeId: '0-1', name: 'city', value: town });
            fields.push({ objectTypeId: '0-1', name: 'town', value: town });
          }
        }
        const sourcePage = this.form.elements.source_page;
        if (sourcePage && sourcePage.value.trim() !== '') {
          fields.push({ objectTypeId: '0-1', name: 'source_page', value: sourcePage.value.trim() });
        }
        const productUrl = this.form.elements.product_to_quote;
        if (productUrl && productUrl.value.trim() !== '') {
          fields.push({ objectTypeId: '0-1', name: 'product_to_quote', value: productUrl.value.trim() });
        }
        return fields;
      }

      buildFullFields(photoUrls) {
        const fields = this.buildBasicFields();
        /* The project questions are optional, so only send the ones that were filled in. */
        ['project_description', 'address'].forEach((name) => {
          const input = this.form.elements[name];
          if (input && input.value.trim() !== '') {
            fields.push({ objectTypeId: '0-1', name: name, value: input.value.trim() });
          }
        });
        if (photoUrls && photoUrls.length > 0) {
          fields.push({ objectTypeId: '0-1', name: 'list_project_photos', value: photoUrls.join('\n') });
        }
        return fields;
      }

      buildHdyhauFields() {
        const fields = [];
        const selected = Array.from(this.form.querySelectorAll('.lsqf__chip-input[name="hdyhau"]:checked'))
          .map((input) => input.value);
        const fallback = (this.dataset.hdyhauDefault || '').trim();
        if (selected.length > 0) {
          fields.push({ objectTypeId: '0-1', name: 'how_did_you_hear', value: selected.join(';') });
        } else if (fallback !== '') {
          fields.push({ objectTypeId: '0-1', name: 'how_did_you_hear', value: fallback });
        }
        const other = this.form.elements.hdyhau_other;
        const typed = other ? other.value.trim() : '';
        if (typed !== '') {
          fields.push({ objectTypeId: '0-1', name: 'hdyhau_other', value: typed });
        }
        if (fields.length === 0) return null;
        const email = this.form.elements.email;
        if (email && email.value.trim() !== '') {
          fields.push({ objectTypeId: '0-1', name: 'email', value: email.value.trim() });
        }
        return fields;
      }

      /*
        HubSpot rejects the whole submission when a property is not on the form, so
        anything not proven to be on it is listed here as droppable: losing one field
        beats losing the lead. Dropping is a last resort — the retry ladder below drops
        only what HubSpot actually named, and logs it so a missing property gets fixed
        in HubSpot rather than silently swallowed on every submission.
      */
      static get OPTIONAL_FIELDS() {
        return ['source_page', 'product_to_quote', 'project_description', 'address', 'list_project_photos', 'zip', 'city', 'town', 'how_did_you_hear'];
      }

      /* Without these there is no lead to save, so they are never dropped — a phone
         HubSpot will not take is reformatted instead. */
      static get ESSENTIAL_FIELDS() {
        return ['firstname', 'email', 'phone'];
      }

      /* Mistyped top-level domains and common provider typos. HubSpot takes these
         without a contact record, so they are refused here instead. */
      static get EMAIL_TYPO_TLDS() {
        return ['con', 'cmo', 'comm', 'coom', 'ocm', 'cim', 'vom', 'xom', 'nte', 'ney', 'rog'];
      }

      static get EMAIL_TYPO_DOMAINS() {
        return ['gmial.com', 'gmai.com', 'gmal.com', 'gmaill.com', 'gmail.co', 'gnail.com', 'hotmial.com', 'hotmai.com', 'yaho.com', 'yahooo.com', 'outlok.com', 'icloud.con'];
      }

      static isEmailAcceptable(input) {
        if (!input.validity.valid) return false;
        const value = input.value.trim().toLowerCase();
        const at = value.lastIndexOf('@');
        if (at < 1) return false;
        const domain = value.slice(at + 1);
        const tld = domain.slice(domain.lastIndexOf('.') + 1);
        return !LsQuoteForm.EMAIL_TYPO_TLDS.includes(tld) && !LsQuoteForm.EMAIL_TYPO_DOMAINS.includes(domain);
      }

      async rejectedFieldNames(response) {
        try {
          const body = await response.clone().text();
          const matches = body.match(/fields\.([A-Za-z0-9_]+)/g) || [];
          return matches.map((match) => match.slice('fields.'.length));
        } catch (error) {
          return [];
        }
      }

      async postToHubspot(fields, formId = this.dataset.formId) {
        const portalId = this.dataset.portalId;
        if (!portalId || !formId) throw new Error('Missing HubSpot configuration');

        let response = await this.sendSubmission(portalId, formId, fields);
        if (response.ok) return;

        const named = await this.rejectedFieldNames(response);
        this.reportRejectedFields(named);

        /* A form whose phone field refuses the leading + still takes bare digits.
           Reformat rather than drop: phone is the one contact detail the lead is
           useless without. */
        if (named.includes('phone')) {
          const reformatted = fields.map((field) =>
            field.name === 'phone'
              ? { objectTypeId: field.objectTypeId, name: 'phone', value: LsQuoteForm.toDigits(field.value) }
              : field
          );
          response = await this.sendSubmission(portalId, formId, reformatted);
          if (response.ok) return;
          fields = reformatted;
        }

        const essential = LsQuoteForm.ESSENTIAL_FIELDS;
        const nonEssential = [...new Set(fields.map((field) => field.name))].filter((name) => !essential.includes(name));
        const ladder = [
          named.filter((name) => !essential.includes(name)),
          LsQuoteForm.OPTIONAL_FIELDS,
          nonEssential
        ];
        for (let index = 0; index < ladder.length; index += 1) {
          const droppable = ladder[index];
          if (droppable.length === 0) continue;
          const retryFields = fields.filter((field) => !droppable.includes(field.name));
          if (retryFields.length === fields.length) continue;
          response = await this.sendSubmission(portalId, formId, retryFields);
          if (response.ok) {
            this.reportDroppedFields(droppable);
            return;
          }
        }

        throw new Error('HubSpot submission failed: ' + response.status);
      }

      reportRejectedFields(names) {
        if (names.length > 0) {
          console.warn('[ls-quote-form] HubSpot rejected these fields:', names.join(', '));
        }
      }

      reportDroppedFields(names) {
        console.warn(
          '[ls-quote-form] Lead accepted only after dropping:',
          names.join(', '),
          '— add these properties to the HubSpot form so they stop being lost.'
        );
      }

      async sendSubmission(portalId, formId, fields) {
        const context = this.buildContext();
        const relay = this.dataset.leadRelayUrl;

        if (relay) {
          try {
            const relayed = await fetch(relay, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                portalId: portalId,
                formId: formId,
                fields: fields,
                context: context,
                turnstileToken: await this.nextToken()
              })
            });
            if (relayed.ok) return relayed;
            /* A 400 means the lead itself is invalid (for example an incorrect email). Sending it
               straight to HubSpot can succeed without creating a contact, so report it instead. */
            if (relayed.status === 400) return relayed;
            console.warn('[ls-quote-form] Lead relay refused, posting to HubSpot directly:', relayed.status);
          } catch (error) {
            console.warn('[ls-quote-form] Lead relay unreachable, posting to HubSpot directly:', error);
          }
        }

        return fetch(
          'https://api.hsforms.com/submissions/v3/integration/submit/' + portalId + '/' + formId,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fields: fields, context: context })
          }
        );
      }

      setButtonLoading(button, loading) {
        if (!button) return;
        button.disabled = loading;
        button.setAttribute('aria-busy', loading ? 'true' : 'false');
      }

      setButtonLabel(button, text) {
        const label = button ? button.querySelector('.ls-btn__label') : null;
        if (!label) return;
        if (!label.dataset.defaultLabel) label.dataset.defaultLabel = label.textContent;
        label.textContent = text || label.dataset.defaultLabel;
      }

      async uploadPhotos() {
        const endpoint = this.dataset.uploadUrl;
        const photos = this.verifiedPhotos();
        this.photosFailed = false;
        if (!endpoint || photos.length === 0) return [];

        const payload = new FormData();
        photos.forEach((file) => payload.append('photos', file));

        this.setUploadProgress(0);
        try {
          const response = await this.sendUpload(endpoint, payload);
          if (response.status < 200 || response.status >= 300) {
            this.photosFailed = true;
            console.warn('[ls-quote-form] Photo upload rejected:', response.status, response.text);
            return [];
          }
          const result = JSON.parse(response.text || '{}');
          return Array.isArray(result.urls) ? result.urls : [];
        } catch (error) {
          this.photosFailed = true;
          console.warn('[ls-quote-form] Photo upload failed:', error);
          return [];
        } finally {
          this.setButtonLabel(this.submitButton, '');
        }
      }

      sendUpload(endpoint, payload) {
        return new Promise((resolve, reject) => {
          const request = new XMLHttpRequest();
          request.open('POST', endpoint);
          request.upload.addEventListener('progress', (event) => {
            if (!event.lengthComputable) return;
            this.setUploadProgress(Math.round((event.loaded / event.total) * 100));
          });
          request.addEventListener('load', () => {
            this.setUploadProgress(100);
            resolve({ status: request.status, text: request.responseText });
          });
          request.addEventListener('error', () => reject(new Error('Network error')));
          request.addEventListener('timeout', () => reject(new Error('Upload timed out')));
          request.addEventListener('abort', () => reject(new Error('Upload aborted')));
          request.send(payload);
        });
      }

      setUploadProgress(percent) {
        const uploading = this.dataset.photosUploadingLabel || '';
        const format = this.dataset.photosProgressFormat || '';
        if (!format || percent <= 0 || percent >= 100) {
          this.setButtonLabel(this.submitButton, uploading);
          return;
        }
        this.setButtonLabel(this.submitButton, format.replace('[percent]', String(percent)));
      }

      hideStatuses() {
        if (this.status) this.status.hidden = true;
      }

      showQuickSuccess() {
        if (this.nextVideo) this.nextVideo.pause();
        this.form.classList.add('lsqf__card--submitted');
        Object.keys(this.panels).forEach((key) => {
          this.panels[key].hidden = key !== 'main';
        });
        if (this.success) this.success.hidden = false;
        this.hideStatuses();
      }

      reportLeadFailure(error) {
        const relay = this.dataset.leadRelayUrl;
        if (!relay) return;
        const endpoint = relay.replace(/\/lead\/?$/, '/lead-failure');
        try {
          fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              formId: this.dataset.formId,
              page: window.location.pathname,
              reason: String((error && error.message) || 'unknown').slice(0, 200),
              at: new Date().toISOString()
            }),
            keepalive: true
          }).catch(() => {});
        } catch (reportError) {
          /* Best effort: a failed report must not change what the visitor sees. */
        }
      }

      async submitLead(fields) {
        try {
          await this.postToHubspot(fields);
        } catch (error) {
          console.warn('[ls-quote-form] Lead post failed, retrying once:', error);
          await this.postToHubspot(fields);
        }
      }

      async onSubmit(event) {
        event.preventDefault();
        this.hideStatuses();
        this.commitTypedZip(true);
        /*
          Backstop for anything that reached submit without tripping the change
          listeners — autofill, a browser-restored form, a scripted fill. In warning
          mode it shows once and the next press goes through; with the fence lock it
          unticks Fences, so the form then asks for another service or the phone.
        */
        if (this.checkFenceArea()) return;
        if (!this.validateMain()) return;

        this.setButtonLoading(this.submitButton, true);
        try {
          const photoUrls = await this.uploadPhotos();
          await this.submitLead(this.buildFullFields(photoUrls));
          this.trackLead();
          this.showPanel('submitted');
          this.showPhotosNotice();
          this.playNextVideo();
        } catch (error) {
          console.error('[ls-quote-form] Lead not sent after retry:', error);
          this.reportLeadFailure(error);
          if (this.status) this.status.hidden = false;
        } finally {
          this.setButtonLoading(this.submitButton, false);
        }
      }

      /*
        Tells Google Tag Manager a lead went in. HubSpot already has it; this is the
        signal the Google Ads and GA4 lead conversions listen for, since the form
        never leaves the page. No contact details are sent, only where and what.
      */
      trackLead() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'quote_form_submit',
          form_page: window.location.pathname,
          form_services: this.selectedProducts().join(','),
        });
      }

      /* ── "What happens next" video ─────────────────────────── */

      /*
        The explainer tells the fence story, so for now it only shows when Fences
        was picked (Jake, Oct 21: the other services get their own cartoon later).
        Which services show it is set in Theme settings > LS Quote Form.
      */
      playNextVideo() {
        const wrap = this.querySelector('[data-next-video-wrap]');
        const video = this.nextVideo;
        if (!wrap || !video) return;
        const services = String(this.dataset.nextVideoServices || '').split(',').filter(Boolean);
        const show = this.selectedProducts().some((service) => services.indexOf(service) !== -1);
        wrap.hidden = !show;
        if (!show || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        video.muted = true;
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      }

      async finishDone(button) {
        const fields = this.buildHdyhauFields();
        const formId = this.dataset.hdyhauFormId;
        if (fields && formId) {
          this.setButtonLoading(button, true);
          try {
            await this.postToHubspot(fields, formId);
          } catch (error) {
            /* The lead is already in — a failed follow-up answer should not block closing. */
          } finally {
            this.setButtonLoading(button, false);
          }
        }
        this.showQuickSuccess();
      }
    }
  );
}
